import React, { useEffect, useRef, useState } from "react";
import Icon from "components/AppIcon";
import { useNavigate } from "react-router-dom";

// Simple animated number for vault balance
function useAnimatedVault(target) {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = prevRef?.current;
    const to = target;
    if (from === to || typeof to !== 'number') return;
    const start = performance.now();
    const duration = 500;
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setDisplay(to);
        prevRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef?.current);
  }, [target]);

  return display;
}

const FactionStatusPanel = ({ faction, isLive }) => {
  const navigate = useNavigate();
  const animatedVault = useAnimatedVault(faction?.vault || 0);
  const [vaultFlash, setVaultFlash] = useState(false);
  const prevVaultRef = useRef(faction?.vault);

  useEffect(() => {
    if (faction?.vault !== prevVaultRef?.current && prevVaultRef?.current !== undefined) {
      setVaultFlash(true);
      setTimeout(() => setVaultFlash(false), 1200);
    }
    prevVaultRef.current = faction?.vault;
  }, [faction?.vault]);

  if (!faction) {
    return (
      <div
        className="rounded-xl border border-border p-4 md:p-5 flex flex-col items-center justify-center gap-3 min-h-[160px]"
        style={{ background: "var(--color-card)" }}
      >
        <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
        <p className="font-caption text-sm text-muted-foreground text-center">
          Not in a faction — join or create one
        </p>
        <button
          onClick={() => navigate('/factions')}
          className="font-caption text-xs text-primary border border-primary/30 px-3 py-1.5 rounded-md hover:bg-primary/10 transition-all duration-250"
        >
          Join or Create
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-border p-4 md:p-5"
      style={{ background: "var(--color-card)" }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-semibold text-foreground">Faction</h3>
        <div className="flex items-center gap-2">
          {isLive && (
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: 'var(--color-success)' }}
            />
          )}
          <span
            className="font-caption text-xs px-2 py-0.5 rounded"
            style={{
              background: `${faction?.color || '#00D9FF'}20`,
              color: faction?.color || '#00D9FF',
              border: `1px solid ${faction?.color || '#00D9FF'}40`,
            }}
          >
            {faction?.rank || 'Member'}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${faction?.color || '#00D9FF'}15`, border: `1px solid ${faction?.color || '#00D9FF'}30` }}
        >
          <Icon name="Shield" size={18} color={faction?.color || '#00D9FF'} />
        </div>
        <div className="min-w-0">
          <p className="font-heading text-sm font-semibold text-foreground truncate">{faction?.name || 'Unknown Faction'}</p>
          <p className="font-caption text-xs text-muted-foreground">{faction?.members || 0} members</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div
          className={`rounded-lg p-2.5 transition-all duration-300 ${
            vaultFlash ? 'ring-1 ring-success/40' : ''
          }`}
          style={{ background: "var(--color-secondary)" }}
        >
          <p className="font-caption text-xs text-muted-foreground mb-0.5">Vault</p>
          <p className="font-heading text-sm font-semibold text-foreground tabular-nums">
            ${Math.round(animatedVault)?.toLocaleString()}
          </p>
        </div>
        <div
          className="rounded-lg p-2.5"
          style={{ background: "var(--color-secondary)" }}
        >
          <p className="font-caption text-xs text-muted-foreground mb-0.5">Territories</p>
          <p className="font-heading text-sm font-semibold text-foreground">{faction?.territories || 0}</p>
        </div>
      </div>
      <div
        className="flex items-center gap-2 p-2 rounded-lg"
        style={{ background: "rgba(0,217,255,0.05)", border: "1px solid rgba(0,217,255,0.1)" }}
      >
        <Icon name="MapPin" size={12} color="var(--color-primary)" />
        <span className="font-caption text-xs text-muted-foreground">
          Territory bonus: <span className="text-primary">+{faction?.territoryBonus || 0}% revenue</span>
        </span>
      </div>
    </div>
  );
};

export default FactionStatusPanel;